import React, { useState, useEffect, useRef } from 'react';
// Cross-platform beep function using Web Audio API
function beep() {
  try {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    const oscillator = ctx.createOscillator();
    oscillator.type = 'sine';
    oscillator.frequency.value = 880;
    oscillator.connect(ctx.destination);
    oscillator.start();
    setTimeout(() => {
      oscillator.stop();
      ctx.close();
    }, 120);
  } catch (e) {
    // fallback: try alert sound
    window && window.navigator && window.navigator.vibrate && window.navigator.vibrate(100);
  }
}
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  TextField,
  Typography,
  IconButton,
  Stack,
  Alert,
  Chip,
  CircularProgress,
  Box,
  Tooltip,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  FormLabel,
} from '@mui/material';
import {
  Close as CloseIcon,
  QrCodeScanner as ScanIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Add as AddRowIcon,
  Delete as DeleteRowIcon,
} from '@mui/icons-material';

/**
 * IMEIEntryDialog
 *
 * Used during Purchase Invoice creation for serialized products.
 * Renders one row per unit for IMEI / Serial / Notes / Warranty entry.
 * Users can add extra rows beyond the initial quantity if needed.
 *
 * Props:
 *   open            {bool}
 *   onClose         {fn}
 *   quantity        {number}       – how many items were purchased (initial rows)
 *   variantName     {string}       – display label
 *   existingItems   {Array}        – pre-filled items from parent state
 *   onSave          {fn(items)}    – called with array of item objects
 */
const EMPTY_ROW = () => ({ imei: '', serialNumber: '', notes: '', warrantyExpiryDate: '' });

const IMEIEntryDialog = ({
  open,
  onClose,
  quantity = 1,
  variantName = '',
  existingItems = [],
  onSave,
}) => {
  const [rows, setRows] = useState([]);
  const [errors, setErrors] = useState({});
  const [entryMode, setEntryMode] = useState('imei'); // 'imei' or 'serial'
  const firstInputRef = useRef(null);

  // Initialise / reset rows when dialog opens
  useEffect(() => {
    if (!open) return;
    const count = Math.max(quantity, existingItems.length, 1);
    const initialRows = Array.from({ length: count }, (_, i) => ({
      imei: existingItems[i]?.imei || '',
      serialNumber: existingItems[i]?.serialNumber || '',
      notes: existingItems[i]?.notes || '',
      warrantyExpiryDate: existingItems[i]?.warrantyExpiryDate || '',
    }));
    setRows(initialRows);
    setErrors({});
  }, [open, quantity, existingItems]);

  const handleChange = (idx, field, value) => {
    // Build the new rows array as it will be after this change
    const updatedRows = rows.map((row, i) => i === idx ? { ...row, [field]: value } : row);
    setRows(updatedRows);
    setErrors((prev) => {
      const next = { ...prev };
      delete next[`${idx}_${field}`];
      return next;
    });

    // Check for duplicate IMEI or Serial Number and beep immediately
    if (field === 'imei' || field === 'serialNumber') {
      const val = value.trim();
      if (val) {
        const values = updatedRows.map(row => row[field].trim());
        if (values.filter(v => v === val).length > 1) {
          beep();
        }
      }
    }
  };

  const handleAddRow = () => {
    // Validate current row for duplicates before adding
    const errs = validate();
    if (Object.keys(errs).length > 0) {
      setErrors(errs);
      // If duplicate error, beep
      if (Object.values(errs).some(e => e.toLowerCase().includes('duplicate'))) {
        beep();
      }
      return;
    }
    setRows((prev) => [...prev, EMPTY_ROW()]);
  };

  const handleRemoveRow = (idx) => {
    setRows((prev) => prev.filter((_, i) => i !== idx));
    setErrors((prev) => {
      const next = {};
      Object.entries(prev).forEach(([key, val]) => {
        const [rowIdx, ...rest] = key.split('_');
        const n = parseInt(rowIdx, 10);
        if (n < idx) next[key] = val;
        else if (n > idx) next[`${n - 1}_${rest.join('_')}`] = val;
      });
      return next;
    });
  };

  const validate = () => {
    const errs = {};
    const imeiSet = new Set();
    const serialSet = new Set();
    
    rows.forEach((row, i) => {
      const imeiVal = row.imei.trim();
      const serialVal = row.serialNumber.trim();
      
      // Validate based on entry mode
      if (entryMode === 'imei') {
        // IMEI mode - IMEI is required
        if (!imeiVal) {
          errs[`${i}_imei`] = 'IMEI is required';
        } else if (imeiSet.has(imeiVal)) {
          errs[`${i}_imei`] = 'Duplicate IMEI';
        } else {
          imeiSet.add(imeiVal);
        }
      } else {
        // Serial Number mode - Serial Number is required
        if (!serialVal) {
          errs[`${i}_serialNumber`] = 'Serial Number is required';
        } else if (serialSet.has(serialVal)) {
          errs[`${i}_serialNumber`] = 'Duplicate Serial Number';
        } else {
          serialSet.add(serialVal);
        }
      }
      
      // Check for duplicates in optional fields
      if (entryMode === 'imei' && serialVal) {
        if (serialSet.has(serialVal)) {
          errs[`${i}_serialNumber`] = 'Duplicate Serial Number';
        } else {
          serialSet.add(serialVal);
        }
      }
      if (entryMode === 'serial' && imeiVal) {
        if (imeiSet.has(imeiVal)) {
          errs[`${i}_imei`] = 'Duplicate IMEI';
        } else {
          imeiSet.add(imeiVal);
        }
      }
    });
    return errs;
  };

  const filledCount = entryMode === 'imei' 
    ? rows.filter((r) => r.imei.trim()).length 
    : rows.filter((r) => r.serialNumber.trim()).length;
  const isComplete = filledCount === rows.length && rows.length > 0;

  const handleSave = () => {
    const errs = validate();
    if (Object.keys(errs).length > 0) {
      setErrors(errs);
      if (Object.values(errs).some(e => e.toLowerCase().includes('duplicate'))) {
        beep();
      }
      return;
    }
    if (onSave) onSave(rows.map((r) => ({ ...r, imei: r.imei.trim(), serialNumber: r.serialNumber.trim() })));
    onClose();
  };

  // Prevent closing on backdrop click or Escape
  const handleDialogClose = (event, reason) => {
    if (reason === 'backdropClick' || reason === 'escapeKeyDown') {
      return;
    }
    onClose();
  };

  return (
    <Dialog 
      open={open} 
      onClose={handleDialogClose} 
      maxWidth="lg" 
      fullWidth 
      PaperProps={{ sx: { borderRadius: 2, minHeight: '600px' } }} 
      disableEscapeKeyDown
    >
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 1 }}>
        <Box>
          <Typography variant="h6" fontWeight={600}>Enter IMEI / Serial Numbers</Typography>
          <Typography variant="body2" color="text.secondary">
            <strong>{variantName}</strong>
            {' — '}purchased qty: <strong>{quantity}</strong>, rows: <strong>{rows.length}</strong>
          </Typography>
        </Box>
        <Stack direction="row" spacing={1} alignItems="center">
          <Chip
            label={`${filledCount} / ${rows.length} entered`}
            color={isComplete ? 'success' : filledCount > 0 ? 'warning' : 'default'}
            size="small"
            icon={isComplete ? <CheckCircleIcon /> : undefined}
          />
          <IconButton onClick={onClose} size="small"><CloseIcon /></IconButton>
        </Stack>
      </DialogTitle>

      <DialogContent dividers sx={{ p: 2 }}>
        {/* Entry Mode Selection */}
        <Box sx={{ mb: 2, p: 2, bgcolor: 'grey.50', borderRadius: 2 }}>
          <FormControl component="fieldset">
            <FormLabel component="legend" sx={{ fontWeight: 600, mb: 1 }}>
              Entry Mode
            </FormLabel>
            <RadioGroup
              row
              value={entryMode}
              onChange={(e) => setEntryMode(e.target.value)}
            >
              <FormControlLabel 
                value="imei" 
                control={<Radio />} 
                label="IMEI (default)" 
              />
              <FormControlLabel 
                value="serial" 
                control={<Radio />} 
                label="Serial Number" 
              />
            </RadioGroup>
            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
              {entryMode === 'imei' 
                ? 'IMEI column is required, Serial Number is optional' 
                : 'Serial Number column is required, IMEI is optional'}
            </Typography>
          </FormControl>
        </Box>

        {rows.length === 0 ? (
          <Box sx={{ p: 3, textAlign: 'center' }}>
            <Typography color="text.secondary">No rows. Click "Add" below to add entries.</Typography>
          </Box>
        ) : (
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow sx={{ bgcolor: 'grey.50' }}>
                <TableCell width={40}>#</TableCell>
                {entryMode === 'imei' && <TableCell><strong>IMEI *</strong></TableCell>}
                {entryMode === 'serial' && <TableCell><strong>Serial Number *</strong></TableCell>}
                {entryMode === 'imei' && <TableCell><strong>Serial Number</strong></TableCell>}
                {entryMode === 'serial' && <TableCell><strong>IMEI</strong></TableCell>}
                <TableCell><strong>Warranty Expiry</strong></TableCell>
                <TableCell><strong>Notes</strong></TableCell>
                <TableCell width={100} align="center">
                  <Button
                    size="small"
                    variant="outlined"
                    color="secondary"
                    onClick={handleAddRow}
                    sx={{ textTransform: 'none', minWidth: 'auto', px: 1 }}
                  >
                    Add
                  </Button>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((row, i) => (
                <TableRow key={i} sx={{ '& td': { py: 0.5 }, bgcolor: i >= quantity ? 'warning.50' : undefined }}>
                  <TableCell>
                    <Typography variant="body2" color={i >= quantity ? 'warning.dark' : 'text.secondary'} fontWeight={i >= quantity ? 600 : 400}>
                      {i + 1}
                    </Typography>
                  </TableCell>
                  
                  {/* Primary field based on mode */}
                  {entryMode === 'imei' ? (
                    <TableCell>
                      <TextField
                        size="small"
                        fullWidth
                        placeholder="123456789012345"
                        value={row.imei}
                        onChange={(e) => handleChange(i, 'imei', e.target.value)}
                        error={Boolean(errors[`${i}_imei`])}
                        helperText={errors[`${i}_imei`]}
                        inputRef={i === 0 ? firstInputRef : undefined}
                        inputProps={{ maxLength: 20 }}
                        onKeyDown={(e) => {
                          if (
                            e.key === 'Enter' &&
                            row.imei.trim() &&
                            i === rows.length - 1
                          ) {
                            e.preventDefault();
                            handleAddRow();
                            setTimeout(() => {
                              const next = document.querySelector(
                                `input[name="imei-row-${rows.length}"]`
                              );
                              if (next) next.focus();
                            }, 100);
                          }
                        }}
                        name={`imei-row-${i}`}
                      />
                    </TableCell>
                  ) : (
                    <TableCell>
                      <TextField
                        size="small"
                        fullWidth
                        placeholder="SN001"
                        value={row.serialNumber}
                        onChange={(e) => handleChange(i, 'serialNumber', e.target.value)}
                        error={Boolean(errors[`${i}_serialNumber`])}
                        helperText={errors[`${i}_serialNumber`]}
                        inputRef={i === 0 ? firstInputRef : undefined}
                        onKeyDown={(e) => {
                          if (
                            e.key === 'Enter' &&
                            row.serialNumber.trim() &&
                            i === rows.length - 1
                          ) {
                            e.preventDefault();
                            handleAddRow();
                            setTimeout(() => {
                              const next = document.querySelector(
                                `input[name="serial-row-${rows.length}"]`
                              );
                              if (next) next.focus();
                            }, 100);
                          }
                        }}
                        name={`serial-row-${i}`}
                      />
                    </TableCell>
                  )}
                  
                  {/* Secondary field based on mode */}
                  {entryMode === 'imei' ? (
                    <TableCell>
                      <TextField
                        size="small"
                        fullWidth
                        placeholder="SN001"
                        value={row.serialNumber}
                        onChange={(e) => handleChange(i, 'serialNumber', e.target.value)}
                        error={Boolean(errors[`${i}_serialNumber`])}
                        helperText={errors[`${i}_serialNumber`]}
                      />
                    </TableCell>
                  ) : (
                    <TableCell>
                      <TextField
                        size="small"
                        fullWidth
                        placeholder="123456789012345"
                        value={row.imei}
                        onChange={(e) => handleChange(i, 'imei', e.target.value)}
                        error={Boolean(errors[`${i}_imei`])}
                        helperText={errors[`${i}_imei`]}
                        inputProps={{ maxLength: 20 }}
                      />
                    </TableCell>
                  )}
                  
                  <TableCell>
                    <TextField
                      size="small"
                      type="date"
                      fullWidth
                      value={row.warrantyExpiryDate}
                      onChange={(e) => handleChange(i, 'warrantyExpiryDate', e.target.value)}
                      InputLabelProps={{ shrink: true }}
                    />
                  </TableCell>
                  <TableCell>
                    <TextField
                      size="small"
                      fullWidth
                      placeholder="e.g. New"
                      value={row.notes}
                      onChange={(e) => handleChange(i, 'notes', e.target.value)}
                    />
                  </TableCell>
                  <TableCell align="center">
                    {rows.length > 1 ? (
                      <Tooltip title="Remove row">
                        <IconButton size="small" color="error" onClick={() => handleRemoveRow(i)}>
                          <DeleteRowIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    ) : (
                      (entryMode === 'imei' ? row.imei.trim() : row.serialNumber.trim())
                        ? <CheckCircleIcon color="success" fontSize="small" />
                        : <ErrorIcon color="disabled" fontSize="small" />
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2, justifyContent: 'space-between' }}>
        <Typography variant="body2" color="text.secondary">
          {isComplete
            ? 'All rows complete.'
            : `${rows.length - filledCount} row(s) still need ${entryMode === 'imei' ? 'an IMEI' : 'a Serial Number'}.`}
        </Typography>
        <Stack direction="row" spacing={1}>
          <Button onClick={onClose}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={filledCount === 0}
          >
            Save {entryMode === 'imei' ? 'IMEIs' : 'Serial Numbers'}
          </Button>
        </Stack>
      </DialogActions>
    </Dialog>
  );
};

export default IMEIEntryDialog;
